import { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Plus, LayoutList, Kanban } from "lucide-react"
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/core"
import { useQueryClient } from "@tanstack/react-query"
import { useDealsBoard, useChangeDealPhase } from "@/hooks/use-deals"
import { useDealTypes } from "@/hooks/use-deal-types"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { formatCurrency } from "@/lib/utils"
import { toast } from "sonner"
import type { DealBoardColumn, DealCard } from "@/lib/types"
import { KanbanColumn, KanbanCardComponent } from "./kanban-column"

export function DealsBoardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: dealTypesData } = useDealTypes()
  const dealTypes = dealTypesData?.data ?? []
  const [selectedDealTypeId, setSelectedDealTypeId] = useState<string | undefined>(undefined)

  const activeDealTypeId = selectedDealTypeId || dealTypes.find(dt => dt.is_default)?.id || dealTypes[0]?.id
  const { data, isLoading } = useDealsBoard(activeDealTypeId)
  const changePhaseMutation = useChangeDealPhase()

  const columns = data?.data ?? []

  // --- DnD state ---
  const [activeCard, setActiveCard] = useState<DealCard | null>(null)
  const [localColumns, setLocalColumns] = useState<DealBoardColumn[] | null>(null)

  const displayColumns = localColumns ?? columns

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  )

  const findColumnForCard = useCallback(
    (cardId: string, cols: DealBoardColumn[]): string | null => {
      for (const col of cols) {
        if (col.deals.some(d => d.id === cardId)) return col.phase.id
      }
      return null
    },
    [],
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id)
      for (const col of columns) {
        const card = col.deals.find(d => d.id === id)
        if (card) {
          setActiveCard(card)
          setLocalColumns(structuredClone(columns))
          break
        }
      }
    },
    [columns],
  )

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event
      if (!over || !localColumns) return

      const activeId = String(active.id)
      const overId = String(over.id)

      const fromColId = findColumnForCard(activeId, localColumns)
      // over.id could be a phase ID (column) or a card ID
      let toColId = findColumnForCard(overId, localColumns)
      if (!toColId) {
        // Check if overId is a phase id (column droppable)
        const isColumn = localColumns.some(c => c.phase.id === overId)
        if (isColumn) toColId = overId
      }

      if (!fromColId || !toColId || fromColId === toColId) return

      setLocalColumns(prev => {
        if (!prev) return prev
        const next = structuredClone(prev)
        const fromCol = next.find(c => c.phase.id === fromColId)!
        const toCol = next.find(c => c.phase.id === toColId)!

        const cardIdx = fromCol.deals.findIndex(d => d.id === activeId)
        if (cardIdx === -1) return prev
        const [card] = fromCol.deals.splice(cardIdx, 1)
        fromCol.count--

        // Find insertion index
        const overIdx = toCol.deals.findIndex(d => d.id === overId)
        if (overIdx >= 0) {
          toCol.deals.splice(overIdx, 0, card)
        } else {
          toCol.deals.push(card)
        }
        toCol.count++

        return next
      })
    },
    [localColumns, findColumnForCard],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveCard(null)

      if (!over || !localColumns) {
        setLocalColumns(null)
        return
      }

      const cardId = String(active.id)
      const newPhaseId = findColumnForCard(cardId, localColumns)
      const oldPhaseId = findColumnForCard(cardId, columns)

      if (!newPhaseId || newPhaseId === oldPhaseId) {
        setLocalColumns(null)
        return
      }

      // Check if target is a terminal phase (won/lost)
      const targetCol = localColumns.find(c => c.phase.id === newPhaseId)
      if (targetCol?.phase.is_won || targetCol?.phase.is_lost) {
        toast.error(
          targetCol.phase.is_won
            ? 'Użyj przycisku "Wygrana" na karcie szansy'
            : 'Użyj przycisku "Przegrana" na karcie szansy',
        )
        setLocalColumns(null)
        return
      }

      // Optimistic: keep localColumns as the displayed state
      const optimisticColumns = structuredClone(localColumns)
      setLocalColumns(null)

      // Set optimistic data in cache
      queryClient.setQueryData(
        ["deals_board", activeDealTypeId],
        (old: typeof data) => old ? { ...old, data: optimisticColumns } : old,
      )

      changePhaseMutation.mutate(
        { id: cardId, phase_id: newPhaseId },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["deals_board"] })
            queryClient.invalidateQueries({ queryKey: ["deals"] })
          },
          onError: () => {
            toast.error("Nie udało się zmienić etapu")
            queryClient.invalidateQueries({ queryKey: ["deals_board"] })
          },
        },
      )
    },
    [localColumns, columns, findColumnForCard, changePhaseMutation, queryClient, activeDealTypeId, data],
  )

  const handleDragCancel = useCallback(() => {
    setActiveCard(null)
    setLocalColumns(null)
  }, [])

  // Summary stats
  const totalDeals = displayColumns.reduce((sum, c) => sum + c.count, 0)
  const totalValue = displayColumns.reduce((sum, c) => sum + Number(c.total_value), 0)
  const totalWeighted = displayColumns.reduce((sum, c) => sum + Number(c.weighted_value), 0)

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="page-title">
            {t("deals.title")}
          </h1>
          {/* View toggle */}
          <div className="flex items-center rounded-md border border-input overflow-hidden">
            <button
              onClick={() => navigate("/deals")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors"
            >
              <LayoutList size={14} />
              {t("deals.listView")}
            </button>
            <button className="flex items-center gap-1.5 bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground transition-colors">
              <Kanban size={14} />
              {t("deals.boardView")}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Deal type selector */}
          {dealTypes.length > 1 && (
            <select
              value={activeDealTypeId || ""}
              onChange={e => setSelectedDealTypeId(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm text-foreground"
            >
              {dealTypes.map(dt => (
                <option key={dt.id} value={dt.id}>{dt.name}</option>
              ))}
            </select>
          )}
          <Button onClick={() => navigate("/deals/new")}>
            <Plus size={16} /> {t("deals.new")}
          </Button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-6 rounded-lg border border-border bg-muted px-4 py-2.5 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {t("deals.totalDeals")}
          </span>
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {totalDeals}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {t("deals.totalValue")}
          </span>
          <span className="text-sm font-semibold font-mono tabular-nums text-foreground">
            {formatCurrency(totalValue)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {t("deals.weightedValue")}
          </span>
          <span className="text-sm font-semibold font-mono tabular-nums text-green-600">
            {formatCurrency(totalWeighted)}
          </span>
        </div>
      </div>

      {/* Kanban Board */}
      {isLoading ? (
        <div className="flex gap-4 flex-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-1 min-w-[260px]">
              <Skeleton className="h-10 w-full mb-3" />
              <div className="space-y-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="flex gap-3 flex-1 overflow-x-auto pb-4">
            {displayColumns
              .filter(col => !col.phase.is_won && !col.phase.is_lost)
              .map(col => (
                <KanbanColumn
                  key={col.phase.id}
                  column={col}
                  onCardClick={id => navigate(`/deals/${id}`)}
                />
              ))}
          </div>

          <DragOverlay dropAnimation={null}>
            {activeCard ? (
              <KanbanCardComponent card={activeCard} isDragOverlay />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
